'use client';

import { useMicrocopy as useMicrocopyContext } from '@contexts/MicrocopyContext';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { useFlags } from '@lib/flags/amplitude/client';
import {
  FEATURE_FLAG_ON,
  FLAG_TREATMENT,
  Flags,
} from '@lib/flags/flagConstants';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { formatCategorySlug } from '@lib/util/category';
import c from 'classnames';
import Image from 'next/image';
import {
  Badge,
  Link,
  Stars,
  Tooltip,
  TooltipArrow,
  useIsTextTruncated,
  useTooltip,
} from 'ui-components';
import {
  DEFAULT_LOCALE,
  formatDisplayableCurrency,
  interpolateStringWithData,
} from 'utils';
import { ProductCardProps } from './ProductCard.types';
import { ProductCardBookmarkButton } from './ProductCardBookmarkButton';
import { ProductCardSaleOverlay } from './ProductCardSaleOverlay';
import { useSaleState } from './useIsActiveSale';

const CATEGORY_ICONS: Record<string, string> = {
  '3d': 'Cube',
  '2d': 'ImageSquare',
  audio: 'Headphones',
  tools: 'Wrench',
  vfx: 'MagicWand',
  templates: 'CopySimple',
  ai: 'Circuitry',
  'add-ons': 'Plug',
  essentials: 'PuzzlePiece',
  industry: 'GearSix',
  sdk: 'Code',
  decentralization: 'ShareNetwork',
  animation: 'FilmStrip',
};

export const ProductCard = ({
  name,
  productId,
  imageUrl,
  publisher,
  rating = 0,
  ratingCount = 0,
  price,
  sale,
  category,
  isVerifiedSolution,
  isNewRelease,
  isUnityAwardsWinner,
  entitlement,
  locale: _locale = DEFAULT_LOCALE,
  showFavorite,
  isFavorite,
  showSaleBadgeOnHover: showSaleBadgeOnHoverProp,
  extraEventHandlers = {},
  className = '',
}: ProductCardProps) => {
  const { onFavorite, ...linkEventHandlers } = extraEventHandlers;
  const microcopy = useMicrocopy('product-card', {
    'product-card.purchased': 'Purchased',
    'product-card.purchased-seats': 'Purchased Seat(s)',
    'product-card.owned': 'You own this asset',
    'product-card.verified-solution-tooltip': 'Verified by Unity',
    'product-card.verified-solution-badge': 'Verified Solution',
    'product-card.new-release-badge': 'New Release',
    'product-card.unity-awards-winner-badge': 'Unity Awards Winner',
    'product-card.free': 'Free',
    'product-card.not-enough-ratings': 'Not enough ratings',
    'product-card.star-rating-label': 'Rating: {{rating}} out of 5 stars',
    'product-card.add-to-favorites': 'Add to favorites',
    'product-card.remove-from-favorites': 'Remove from favorites',
  });

  const { searchValuesMicrocopy } =
    useMicrocopyContext<{
      searchValuesMicrocopy?: MicrocopyItem[];
    }>() ?? {};

  const getCategoryDisplayName = (categoryKey?: string): string | undefined => {
    if (!categoryKey) return undefined;

    // Try microcopy lookup first
    if (searchValuesMicrocopy) {
      const categoryResourceBundle = microcopySetToResourceBundles(
        searchValuesMicrocopy
      ).ec_category;
      const microcopyValue =
        categoryResourceBundle?.[categoryKey.toLowerCase()];
      if (microcopyValue) {
        return microcopyValue;
      }
    }

    // Fallback: format the slug as display name
    return formatCategorySlug(categoryKey);
  };

  const { active, upcoming } = useSaleState(sale);

  const {
    context,
    isOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
    arrowRef,
  } = useTooltip({
    offset: 0,
    arrow: true,
  });

  const isTextTruncated = useIsTextTruncated(
    refs.reference as React.RefObject<HTMLDivElement>
  );

  const { flags } = useFlags();
  const openInNewTabFlag = flags[Flags.OPEN_RESULTS_IN_NEW_TAB];
  const shouldOpenProductInNewTab = openInNewTabFlag?.value === FEATURE_FLAG_ON;

  const saleBadgeOnHoverFlag = flags[Flags.SALE_BADGE_ON_HOVER];
  const showSaleBadgeOnHover =
    showSaleBadgeOnHoverProp !== undefined
      ? showSaleBadgeOnHoverProp
      : saleBadgeOnHoverFlag?.value === FLAG_TREATMENT;

  const isOwned = !!entitlement;
  const showSaleOverlay = (active || upcoming) && sale && !isOwned;

  const currentPriceFormatted = formatDisplayableCurrency(
    active ? price.currentPrice : price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );
  const originalPriceFormatted = formatDisplayableCurrency(
    price.originalPrice,
    DEFAULT_LOCALE,
    price.currency
  );

  const roundedRating = Math.round(rating * 10) / 10;
  const hasRating = ratingCount > 0 && rating > 0;

  return (
    <article
      data-test={`search-results-product-card-${productId}`}
      className={c(
        'group relative flex w-full flex-col rounded-lg bg-white transition-shadow duration-200 hover:shadow-lg',
        className
      )}
    >
      <div className="relative aspect-3/2 overflow-hidden rounded-t-lg">
        <Image
          fill
          sizes="50vw, (min-width: 768px) 25vw, (min-width: 1024px) 33vw"
          alt=""
          aria-hidden={true}
          className={c('object-cover', isOwned && 'opacity-40')}
          src={imageUrl}
        />
        {showSaleOverlay && (
          <ProductCardSaleOverlay
            sale={sale}
            isActive={active}
            showOnHoverOnly={showSaleBadgeOnHover}
          />
        )}
      </div>

      <div className="flex h-40 flex-col rounded-b-lg border-x border-b border-grey-200 px-3 pb-3 transition-colors duration-200 group-hover:border-transparent">
        <div
          className={c(
            'flex h-full flex-col gap-3',
            isOwned && 'opacity-40 group-hover:opacity-100'
          )}
        >
          <div className="mt-3 flex h-5 flex-nowrap gap-1 overflow-hidden">
            {category && (
              <Badge
                text={getCategoryDisplayName(category) ?? category}
                iconName={
                  CATEGORY_ICONS[category.toLowerCase()] ?? 'PuzzlePiece'
                }
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test={`product-card-tag-category-${category.toLowerCase()}`}
              />
            )}
            {isVerifiedSolution && (
              <Badge
                text={microcopy['product-card.verified-solution-badge']}
                tooltip={microcopy['product-card.verified-solution-tooltip']}
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test="product-card-tag-verified-solution"
              />
            )}
            {isNewRelease && (
              <Badge
                text={microcopy['product-card.new-release-badge']}
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test="product-card-tag-new-release"
              />
            )}
            {isUnityAwardsWinner && (
              <Badge
                text={microcopy['product-card.unity-awards-winner-badge']}
                iconName="Star"
                className="rounded py-0 leading-5 tracking-tight text-grey-700"
                data-test="product-card-tag-unity-awards"
              />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <Link
              href={`/packages/package/${productId}`}
              data-test="product-card-name"
              target={shouldOpenProductInNewTab ? '_blank' : undefined}
              className="focus-outline rounded after:absolute after:inset-0"
              {...linkEventHandlers}
            >
              <span
                className="relative z-10 line-clamp-1 h-5 text-sm leading-5 tracking-tight text-grey-900"
                ref={refs.setReference}
                {...getReferenceProps()}
              >
                {name}
              </span>
            </Link>

            {isOpen && isTextTruncated && (
              <div
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <TooltipArrow arrowRef={arrowRef} context={context} />
                <Tooltip label={name} className="max-w-56" />
              </div>
            )}

            {hasRating ? (
              <div className="flex h-5 items-center gap-1 text-sm leading-5 text-grey-900">
                <Stars
                  value={roundedRating}
                  width={16}
                  ariaLabel={interpolateStringWithData(
                    microcopy['product-card.star-rating-label'],
                    { rating: roundedRating.toString() }
                  )}
                />
                <span className="text-grey-900" data-test="product-rating">
                  {roundedRating}
                </span>
                <span
                  className="text-grey-600"
                  data-test="product-rating-count"
                >
                  ({ratingCount})
                </span>
              </div>
            ) : (
              <div className="h-5 text-sm leading-5 text-grey-600">
                {microcopy['product-card.not-enough-ratings']}
              </div>
            )}

            <div className="h-5">
              {publisher && (
                <Link
                  data-test="product-card-publisher"
                  target={shouldOpenProductInNewTab ? '_blank' : undefined}
                  className="focus-outline relative z-10 block truncate rounded text-sm leading-5 text-grey-600 hover:text-blue-hover"
                  href={`/publishers/${publisher.id}`}
                >
                  {publisher.name}
                </Link>
              )}
            </div>

            {isOwned ? (
              <div
                className="h-5 text-sm leading-5 text-grey-900"
                data-test="product-card-purchased-label"
              >
                <span>{microcopy['product-card.owned']}</span>
              </div>
            ) : (
              <div
                className="flex h-5 items-center gap-2"
                data-test="search-results-price"
              >
                {price.currentPrice === 0 ? (
                  <span
                    data-test="product-card-current-price"
                    className="text-sm leading-5 text-grey-900"
                  >
                    {microcopy['product-card.free']}
                  </span>
                ) : (
                  <>
                    <span
                      className="text-sm leading-5 text-grey-900"
                      data-test="product-card-current-price"
                    >
                      {currentPriceFormatted}
                    </span>
                    {active && price.discountPercent !== undefined && (
                      <>
                        <span
                          className="text-sm leading-5 text-grey-600 line-through"
                          data-test="product-card-original-price"
                        >
                          {originalPriceFormatted}
                        </span>
                        <span
                          className="rounded bg-red-200 px-1 text-xs leading-5 text-red-600"
                          data-test="product-card-discount"
                        >
                          -{price.discountPercent}%
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showFavorite && isFavorite != null && (
        <ProductCardBookmarkButton
          name={name}
          isFavorite={isFavorite}
          addToFavoritesLabel={microcopy['product-card.add-to-favorites']}
          removeFromFavoritesLabel={
            microcopy['product-card.remove-from-favorites']
          }
          onFavorite={onFavorite}
        />
      )}
    </article>
  );
};
